import { useAtomValue } from 'jotai';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { PermissionKey } from '../../../../../services/PermissionAndRolesKeys';
import { PermissionService } from '../../../../../services/PermissionService';
import { getRoundLabel } from '../../../../../util/preference-utils';
import { ViewTypeByData } from '../../../../../data-models/view-config.data-model';
import { selectedViewPFJ } from '../../../../../pages/PortfolioOverview/state/ViewStateJ';
import { SelectItem, ViewItem } from '../../../../../types';
import { MaggieFeatureFlags } from '../../../../../util/feature-flags';
import { CollapsibleViewSection } from '../NavigationWrapper';
import { Options } from './Options';

interface Props {
  items: ViewItem[];
  onItemSelect: (item: SelectItem) => void;
  onReorder: (reorderedItems: SelectItem[]) => void;
}

// A section reorder only touches the items in that section. Splice the new
// slice order back into the flat list in place so views from other (possibly
// hidden) sections keep their persisted positions.
export function mergeReorderedSection(allItems: ViewItem[], reorderedSection: ViewItem[]): ViewItem[] {
  const sectionIds = new Set(reorderedSection.map((item) => item.id));
  const queue = [...reorderedSection];
  return allItems.map((item) => (sectionIds.has(item.id) ? queue.shift()! : item));
}

export const ViewOptions = ({ items, onItemSelect, onReorder }: Props) => {
  const [itemsState, setItemsState] = useState(items);
  const selectedView = useAtomValue(selectedViewPFJ);
  const { showFundReporting, showPeopleReporting, showSecurityView } = useFlags<MaggieFeatureFlags>();

  const companyItems = useMemo(
    () => itemsState.filter((i) => i.viewType === ViewTypeByData.company),
    [itemsState]
  );

  const fundItems = useMemo(() => itemsState.filter((i) => i.viewType === ViewTypeByData.fund), [itemsState]);

  const transactionItems = useMemo(
    () => itemsState.filter((i) => i.viewType === ViewTypeByData.transaction),
    [itemsState]
  );

  const peopleItems = useMemo(
    () => itemsState.filter((i) => i.viewType === ViewTypeByData.people),
    [itemsState]
  );

  const multiperiodItems = useMemo(
    () => itemsState.filter((i) => i.viewType === ViewTypeByData.multiperiod),
    [itemsState]
  );

  const securityItems = useMemo(
    () => itemsState.filter((i) => i.viewType === ViewTypeByData.security),
    [itemsState]
  );

  useEffect(() => {
    setItemsState(items);
  }, [items]);

  const handleSectionReorder = useCallback(
    (reorderedSection: ViewItem[]) => {
      const merged = mergeReorderedSection(itemsState, reorderedSection);
      setItemsState(merged);
      onReorder(merged);
    },
    [itemsState, onReorder]
  );

  const selectedViewId = selectedView?.viewId ?? '';

  return (
    <>
      {showFundReporting && PermissionService.get().hasPermission(PermissionKey.hasFundProfilesModule) && (
        <ViewList
          items={fundItems}
          title={'FUNDS VIEWS'}
          onItemSelect={onItemSelect}
          onReorder={handleSectionReorder}
          selectedViewId={selectedViewId}
        />
      )}
      <ViewList
        items={companyItems}
        title={'COMPANY VIEWS'}
        onItemSelect={onItemSelect}
        onReorder={handleSectionReorder}
        selectedViewId={selectedViewId}
      />
      <ViewList
        items={transactionItems}
        title={`${getRoundLabel().toUpperCase()} VIEWS`}
        onItemSelect={onItemSelect}
        onReorder={handleSectionReorder}
        selectedViewId={selectedViewId}
      />
      {showSecurityView && PermissionService.get().hasPermission(PermissionKey.canViewSecurityViews) && (
        <ViewList
          items={securityItems}
          title={'SECURITY VIEWS'}
          onItemSelect={onItemSelect}
          onReorder={handleSectionReorder}
          selectedViewId={selectedViewId}
        />
      )}
      {showPeopleReporting && (
        <ViewList
          items={peopleItems}
          title={'TALENT VIEWS'}
          onItemSelect={onItemSelect}
          onReorder={handleSectionReorder}
          selectedViewId={selectedViewId}
        />
      )}
      {PermissionService.get().hasPermission(PermissionKey.canViewMultiperiod) && (
        <ViewList
          items={multiperiodItems}
          title={'MULTI-PERIOD REPORTING'}
          onItemSelect={onItemSelect}
          onReorder={handleSectionReorder}
          selectedViewId={selectedViewId}
        />
      )}
    </>
  );
};

interface IViewListProps {
  items: ViewItem[];
  onItemSelect: (item: SelectItem) => void;
  onReorder: (reorderedItems: ViewItem[]) => void;
  selectedViewId: string;
  title: string;
}

function ViewList({ title, items, onItemSelect, onReorder, selectedViewId }: IViewListProps) {
  const defaultExpanded = useMemo(() => {
    return items.some((item) => item.id === selectedViewId);
  }, [items, selectedViewId]);

  if (items.length === 0) {
    return null;
  }

  return (
    <CollapsibleViewSection key={selectedViewId} title={title} defaultExpanded={defaultExpanded}>
      <Options
        items={items}
        onItemSelect={onItemSelect}
        onReorder={onReorder}
        style={{ maxHeight: 'fit-content', paddingBlock: '0.5rem' }}
      />
    </CollapsibleViewSection>
  );
}
